import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import TermsModal from "../components/TermsModal";
import TurnstileWidget from "../components/TurnstileWidget";
import { TERMS_VERSION } from "../lib/terms";

// Client-side free-domain list — authoritative decision still happens on the
// backend; this is purely so Step 3 can show the correct workspace fields
// without a round-trip.
const FREE_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.co.uk", "yahoo.com.au",
  "ymail.com", "outlook.com", "hotmail.com", "hotmail.co.uk", "live.com",
  "msn.com", "icloud.com", "me.com", "mac.com", "aol.com", "protonmail.com",
  "proton.me", "pm.me", "gmx.com", "gmx.net", "mail.com", "zoho.com",
  "yandex.com", "yandex.ru", "fastmail.com", "tutanota.com", "qq.com", "163.com",
]);

const RESEND_COOLDOWN_SECONDS = 60;

export default function Register() {
  const { acceptToken } = useAuth();
  const navigate = useNavigate();

  // Wizard state. Legal values: "email" → "otp" → "profile".
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [verificationId, setVerificationId] = useState(null);
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  // Step-1 Turnstile
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileWrapRef = useRef(null);
  const onTurnstileToken = useCallback((t) => setTurnstileToken(t), []);
  const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
  // Honeypot — real users leave this empty.
  const [honeypot, setHoneypot] = useState("");

  // Step-3 state
  const [form, setForm] = useState({ name: "", password: "", phone: "", address: "", org_name: "", join_code: "" });
  const [mode, setMode] = useState("create");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const domain = useMemo(() => (email.split("@")[1] || "").toLowerCase().trim(), [email]);
  const isFree = !!domain && FREE_DOMAINS.has(domain);
  const isCorporate = !!domain && !FREE_DOMAINS.has(domain);
  const addressNeeded = isCorporate || (isFree && mode === "create");

  // Resend-countdown timer so the button is correctly disabled.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const resetTurnstile = () => {
    try { turnstileWrapRef.current?.querySelector('[data-testid="turnstile-widget"]')?.__resetTurnstile?.(); } catch { /* widget may already be gone */ }
    setTurnstileToken(null);
  };

  // -------- STEP 1: send OTP --------
  const requestOtp = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !turnstileToken) { toast.error("Please complete the human verification"); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/signup/request-otp", {
        email: email.trim(),
        turnstile_token: turnstileToken || undefined,
        website: honeypot || undefined,
      });
      // Decoy IDs for already-registered emails use a known prefix. We STILL
      // move to Step 2 so bots learn nothing from the UI difference — a real
      // user who already has an account will get stuck at the OTP screen
      // (no code arrives) and will see the login link we show below the form.
      setVerificationId(r.data.verification_id);
      setStep("otp");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success("We've sent a 6-digit code to your email.");
    } catch (err) {
      toast.error(apiError(err, "Could not send verification code"));
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  // -------- STEP 2: verify OTP --------
  const verifyOtp = async (e) => {
    e.preventDefault();
    const code = (otp || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) { toast.error("Please enter the 6-digit code from your email"); return; }
    setLoading(true);
    try {
      await api.post("/auth/signup/verify-otp", { verification_id: verificationId, code });
      toast.success("Email verified — one more step.");
      setStep("profile");
    } catch (err) {
      toast.error(apiError(err, "Code did not match"));
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      await api.post("/auth/signup/resend", { verification_id: verificationId });
      toast.success("New code sent — check your inbox.");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setOtp("");
    } catch (err) {
      toast.error(apiError(err, "Could not resend code"));
    } finally {
      setLoading(false);
    }
  };

  // -------- STEP 3: complete signup --------
  const completeSignup = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) { toast.error("Please accept the Terms & Conditions to continue"); return; }
    if (!/^\+/.test(form.phone)) { toast.error("Phone must start with + and country code, e.g. +61412345678"); return; }
    if (addressNeeded && form.address.trim().length < 8) { toast.error("Please provide a complete physical address"); return; }
    setLoading(true);
    try {
      const payload = {
        verification_id: verificationId,
        name: form.name.trim(),
        password: form.password,
        phone: form.phone.trim(),
        accepted_terms_version: TERMS_VERSION,
      };
      if (addressNeeded) payload.address = form.address.trim();
      if (isFree) {
        if (mode === "create") payload.org_name = form.org_name.trim();
        else payload.join_code = form.join_code.trim().toUpperCase();
      } else if (isCorporate && form.org_name.trim()) {
        payload.org_name = form.org_name.trim();
      }
      const r = await api.post("/auth/signup/complete", payload);
      // Backend set the auth cookie + returned token. Hydrate local state so
      // route guards see the logged-in user, then redirect.
      sessionStorage.setItem("calibrate_signup_welcome", "1");
      acceptToken(r.data.token, r.data.user);
      toast.success(`Welcome aboard, ${r.data.user.name.split(" ")[0]}!`);
      navigate("/welcome", { replace: true, state: { fromSignup: true } });
    } catch (err) {
      toast.error(apiError(err, "Could not finish signup"));
    } finally {
      setLoading(false);
    }
  };

  // -------- RENDER --------
  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="register-page">
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-4">
        [{step === "email" ? "01" : step === "otp" ? "02" : "03"}] {step === "email" ? "Verify your email" : step === "otp" ? "Enter your code" : "Finish signup"}
      </div>
      <h1 className="font-display font-bold text-5xl mb-6">
        {step === "email" && "Register"}
        {step === "otp" && "Check your email"}
        {step === "profile" && "A few more details"}
      </h1>

      {/* STEP INDICATOR */}
      <div className="flex items-center gap-2 mb-8 font-mono-cal text-[10px] uppercase tracking-widest" data-testid="register-steps">
        <span className={`${step === "email" ? "text-[#FF4F00]" : "text-[#111]"}`}>1 · Email</span>
        <span className="text-[#999]">→</span>
        <span className={`${step === "otp" ? "text-[#FF4F00]" : step === "profile" ? "text-[#111]" : "text-[#999]"}`}>2 · Code</span>
        <span className="text-[#999]">→</span>
        <span className={`${step === "profile" ? "text-[#FF4F00]" : "text-[#999]"}`}>3 · Profile</span>
      </div>

      {/* ---------- STEP 1: EMAIL ---------- */}
      {step === "email" && (
        <form onSubmit={requestOtp} className="cal-card p-8 space-y-5" data-testid="register-step1-form">
          <div>
            <label className="cal-label">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="cal-input" placeholder="you@company.com" data-testid="register-email" autoFocus/>
            {isCorporate && (
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#00A36C] mt-2" data-testid="domain-hint-corporate">
                [DOMAIN] {domain} — you'll be matched to your company workspace
              </div>
            )}
            {isFree && (
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mt-2" data-testid="domain-hint-free">
                [PERSONAL EMAIL] you'll nominate an org or use a join code at the next step
              </div>
            )}
            <p className="text-xs text-[#666] mt-3">We'll send you a 6-digit code to prove you own this address. No account is created until you verify.</p>
          </div>

          {/* Honeypot — hidden from humans, bots fill it. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
            <label>Website (leave blank)</label>
            <input type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} data-testid="register-honeypot"/>
          </div>

          {TURNSTILE_SITE_KEY && (
            <div ref={turnstileWrapRef} data-testid="register-turnstile">
              <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} onToken={onTurnstileToken} />
            </div>
          )}

          <button disabled={loading || (TURNSTILE_SITE_KEY && !turnstileToken)} className="cal-btn w-full" data-testid="register-request-otp">
            {loading ? "Sending…" : "Send verification code →"}
          </button>
        </form>
      )}

      {/* ---------- STEP 2: OTP ---------- */}
      {step === "otp" && (
        <form onSubmit={verifyOtp} className="cal-card p-8 space-y-5" data-testid="register-step2-form">
          <div>
            <p className="text-sm text-[#333] mb-5">
              We sent a 6-digit code to <strong className="font-mono-cal">{email}</strong>. It expires in <strong>10 minutes</strong>.
            </p>
            <label className="cal-label">Verification code</label>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*" required
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="cal-input font-mono-cal text-3xl tracking-[0.4em] text-center"
              placeholder="000000"
              data-testid="register-otp-input"
              autoFocus
              autoComplete="one-time-code"
            />
            <p className="text-xs text-[#666] mt-2">Can't find it? Check your spam folder.</p>
          </div>

          <button disabled={loading || otp.length !== 6} className="cal-btn w-full" data-testid="register-verify-otp">
            {loading ? "Checking…" : "Verify code →"}
          </button>

          <div className="flex items-center justify-between pt-2 border-t border-[#111]/10">
            <button type="button" onClick={() => { setStep("email"); setOtp(""); resetTurnstile(); }}
              className="font-mono-cal text-[11px] uppercase tracking-widest text-[#666] hover:text-[#FF4F00]"
              data-testid="register-change-email">
              ← Use a different email
            </button>
            <button type="button" onClick={resendOtp} disabled={loading || resendCooldown > 0}
              className="font-mono-cal text-[11px] uppercase tracking-widest text-[#FF4F00] disabled:text-[#999]"
              data-testid="register-resend-otp">
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
            </button>
          </div>

          <p className="text-xs text-[#666] text-center">
            Already have an account? <Link to="/login" className="underline hover:text-[#FF4F00]">Sign in</Link>
          </p>
        </form>
      )}

      {/* ---------- STEP 3: PROFILE ---------- */}
      {step === "profile" && (
        <form onSubmit={completeSignup} className="cal-card p-8 space-y-5" data-testid="register-step3-form">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#00A36C]">
            ✓ Email verified · <span className="text-[#111]">{email}</span>
          </div>

          <div>
            <label className="cal-label">Full name</label>
            <input required value={form.name} onChange={upd("name")} className="cal-input" data-testid="register-name" autoFocus/>
          </div>
          <div>
            <label className="cal-label">Password (min 6)</label>
            <input type="password" required minLength={6} value={form.password} onChange={upd("password")} className="cal-input" data-testid="register-password" />
          </div>
          <div>
            <label className="cal-label">Mobile phone (international format)</label>
            <input type="tel" required value={form.phone} onChange={upd("phone")}
              placeholder="+61412345678" className="cal-input font-mono-cal" data-testid="register-phone"/>
            <p className="text-xs text-[#666] mt-1">Must include + and country code. Used for account recovery and security alerts.</p>
          </div>

          {isFree && (
            <div className="border border-[#111]/20 p-4 space-y-3" data-testid="free-domain-block">
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">[WORKSPACE]</div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setMode("create")} className={`flex-1 cal-btn ${mode==="create"?"":"cal-btn-ghost"}`} data-testid="mode-create">Create new</button>
                <button type="button" onClick={() => setMode("join")} className={`flex-1 cal-btn ${mode==="join"?"":"cal-btn-ghost"}`} data-testid="mode-join">Join existing</button>
              </div>
              {mode === "create" ? (
                <div>
                  <label className="cal-label">Organization name</label>
                  <input required value={form.org_name} onChange={upd("org_name")} placeholder="e.g. Janes Plumbing" className="cal-input" data-testid="register-org-name"/>
                  <p className="text-xs text-[#666] mt-1">You'll become the admin of this new workspace.</p>
                </div>
              ) : (
                <div>
                  <label className="cal-label">Join code</label>
                  <input required value={form.join_code} onChange={upd("join_code")} placeholder="6-char code from your admin" maxLength={12} className="cal-input font-mono-cal uppercase tracking-widest" data-testid="register-join-code"/>
                  <p className="text-xs text-[#666] mt-1">Your admin will need to approve you before you can run calibrations.</p>
                </div>
              )}
            </div>
          )}

          {isCorporate && (
            <div>
              <label className="cal-label">Organization name (optional)</label>
              <input value={form.org_name} onChange={upd("org_name")} placeholder="Leave blank to use your domain" className="cal-input" data-testid="register-org-name-corp"/>
              <p className="text-xs text-[#666] mt-1">If you're the first {domain} user, this names your workspace. Otherwise it's ignored and you'll join your existing org pending approval.</p>
            </div>
          )}

          {addressNeeded && (
            <div>
              <label className="cal-label">Physical address</label>
              <textarea required rows={2}
                value={form.address} onChange={upd("address")}
                placeholder="Street, city, state/region, country"
                className="cal-input" data-testid="register-address"/>
              <p className="text-xs text-[#666] mt-1">Required to register a new organization. Used for billing and verification.</p>
            </div>
          )}

          <button disabled={loading} className="cal-btn w-full" data-testid="register-submit">
            {loading ? "Creating account…" : "Create account & sign in →"}
          </button>

          <label className="flex items-start gap-3 cursor-pointer pt-1" data-testid="terms-acceptance-block">
            <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#FF4F00]" data-testid="register-accept-terms" required/>
            <span className="text-xs text-[#333] leading-relaxed">
              I have read and agree to the{" "}
              <button type="button" onClick={() => setTermsOpen(true)}
                className="underline hover:text-[#FF4F00] font-mono-cal" data-testid="register-view-terms-link">
                CALIBRATE Terms &amp; Conditions
              </button>
              {" "}(v{TERMS_VERSION}). I confirm I am authorised to bind my organization to this agreement.
            </span>
          </label>
        </form>
      )}

      <p className="mt-6 text-sm text-[#666]">
        Employees don't need an account — you'll send them a secret invite link per calibration.
      </p>
      {step === "email" && (
        <p className="mt-2 text-sm text-[#666]">
          Have an account? <Link to="/login" className="underline hover:text-[#FF4F00]" data-testid="link-login">Sign in</Link>
        </p>
      )}

      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} onAccept={() => setAcceptedTerms(true)}/>
    </div>
  );
}
